import React from 'react';
import { useTheme } from '@mui/material/styles';

export type NorthIndianPlanetLabel = {
  abbr: string;
  degree?: number | null;
  status?: string;
  color: string;
  highlighted?: boolean;
  title?: string;
  isUpagraha?: boolean;
  isTransit?: boolean;
};

export type NorthIndianHouseLabel = {
  id: number;
  houseLabel?: string;
  actualHouseLabel?: string;
  signNumber: number | string;
  signLabel?: string;
  secondaryLabel?: string;
  lagnaMarker?: string;
  planets: NorthIndianPlanetLabel[];
};

type NorthIndianChartRendererProps = {
  houses: NorthIndianHouseLabel[];
  size?: number;
  borderColor?: string;
  gridColor?: string;
  houseColor?: string;
  signColor?: string;
  backgroundColor?: string;
  showBrand?: boolean;
  showHouseLabels?: boolean;
  style?: React.CSSProperties;
};

const CHART_SIZE = 400;

export const NORTH_INDIAN_HOUSE_POSITIONS = [
  { id: 1, x: 50, y: 20 },
  { id: 2, x: 25, y: 5 },
  { id: 3, x: 5, y: 20 },
  { id: 4, x: 20, y: 50 },
  { id: 5, x: 5, y: 80 },
  { id: 6, x: 25, y: 95 },
  { id: 7, x: 50, y: 80 },
  { id: 8, x: 75, y: 95 },
  { id: 9, x: 95, y: 80 },
  { id: 10, x: 80, y: 50 },
  { id: 11, x: 95, y: 20 },
  { id: 12, x: 75, y: 5 },
];

export const NORTH_INDIAN_RASHI_POSITIONS: Record<number, { x: number; y: number }> = {
  1: { x: 50, y: 25 },
  2: { x: 36, y: 10 },
  3: { x: 12, y: 27 },
  4: { x: 34, y: 48 },
  5: { x: 12, y: 73 },
  6: { x: 36, y: 90 },
  7: { x: 50, y: 73 },
  8: { x: 64, y: 90 },
  9: { x: 88, y: 73 },
  10: { x: 66, y: 48 },
  11: { x: 88, y: 27 },
  12: { x: 64, y: 10 },
};

const getPlanetTextStyle = (total: number) => {
  if (total >= 7) return { fontSize: 13, statusFontSize: 7, degreeFontSize: 8, lineGap: 16 };
  if (total >= 5) return { fontSize: 14, statusFontSize: 7, degreeFontSize: 8, lineGap: 18 };
  if (total >= 4) return { fontSize: 15, statusFontSize: 8, degreeFontSize: 9, lineGap: 20 };
  return { fontSize: 17, statusFontSize: 9, degreeFontSize: 10, lineGap: 24 };
};

const getPlanetTextY = (houseY: number, index: number, total: number) => {
  const baseY = (houseY / 100) * CHART_SIZE;
  const { lineGap } = getPlanetTextStyle(total);

  if (houseY > 20 && houseY < 80) {
    return baseY + 12 - ((total - 1) * lineGap) / 2 + index * lineGap;
  }

  if (houseY >= 80) {
    return baseY - 14 - (total - 1 - index) * lineGap;
  }

  return baseY + 24 + index * lineGap;
};

const byId = (houses: NorthIndianHouseLabel[]) => (
  houses.reduce<Record<number, NorthIndianHouseLabel>>((acc, house) => {
    acc[house.id] = house;
    return acc;
  }, {})
);

const hexToRgb = (hex: string) => {
  const clean = String(hex || '').replace('#', '').slice(0, 6);
  if (clean.length !== 6) return null;
  const value = Number.parseInt(clean, 16);
  if (!Number.isFinite(value)) return null;
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};

const isLowContrastOnDark = (color: string) => {
  const rgb = hexToRgb(color);
  if (!rgb) return false;
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance < 0.45;
};

const darkPlanetFallback = (color: string) => {
  if (color === '#d32f2f') return '#fca5a5';
  if (color === '#7b1fa2') return '#d8b4fe';
  if (color === '#e65100') return '#fdba74';
  if (color === '#455a64') return '#cbd5e1';
  if (color === '#00897b') return '#5eead4';
  return isLowContrastOnDark(color) ? '#bbf7d0' : color;
};

const NorthIndianChartRenderer: React.FC<NorthIndianChartRendererProps> = ({
  houses,
  size = 400,
  borderColor,
  gridColor,
  houseColor,
  signColor,
  backgroundColor,
  showBrand = true,
  showHouseLabels = false,
  style,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const palette = {
    background: backgroundColor || (isDark ? '#071f12' : '#ffffff'),
    border: borderColor || (isDark ? '#86efac' : 'black'),
    grid: gridColor || (isDark ? '#4ade80' : 'gray'),
    house: houseColor || (isDark ? '#d1fae5' : 'blue'),
    sign: signColor || (isDark ? '#86efac' : '#de9f33'),
    actualHouse: isDark ? '#cbd5e1' : 'gray',
    secondary: isDark ? '#d8b4fe' : 'purple',
    brand: isDark ? '#bbf7d0' : '#2e7d32',
  };
  const houseMap = byId(houses);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
      style={{ border: `2px solid ${palette.border}`, backgroundColor: palette.background, ...style }}
    >
      <rect x="0" y="0" width={CHART_SIZE} height={CHART_SIZE} fill={palette.background} />
      <g stroke={palette.grid} strokeWidth="1">
        <line x1="0" y1="0" x2="400" y2="400" />
        <line x1="400" y1="0" x2="0" y2="400" />
        <line x1="200" y1="0" x2="0" y2="200" />
        <line x1="0" y1="200" x2="200" y2="400" />
        <line x1="200" y1="400" x2="400" y2="200" />
        <line x1="400" y1="200" x2="200" y2="0" />
      </g>

      {showBrand && (
        <g opacity="0.2">
          <text
            x="200"
            y="207"
            textAnchor="middle"
            dominantBaseline="middle"
            fontWeight="900"
            aria-hidden="true"
          >
            <tspan fontSize="38" fill={palette.brand}>K</tspan>
            <tspan fontSize="20" fill="#f9a825" dx="3" dy="-9">★</tspan>
          </text>
        </g>
      )}

      {NORTH_INDIAN_HOUSE_POSITIONS.map((position) => {
        const house = houseMap[position.id];
        if (!house) return null;

        const houseX = (position.x / 100) * CHART_SIZE;
        const houseY = (position.y / 100) * CHART_SIZE;
        const planetX = Math.min(368, Math.max(32, houseX));
        const rashiPosition = NORTH_INDIAN_RASHI_POSITIONS[position.id] || position;
        const rashiX = (rashiPosition.x / 100) * CHART_SIZE;
        const rashiY = (rashiPosition.y / 100) * CHART_SIZE;

        return (
          <g key={position.id}>
            {showHouseLabels ? (
              <text x={houseX - 14} y={houseY - 8} fill={palette.house} fontSize="10" textAnchor="middle">
                {house.houseLabel || `H${house.id}`}
              </text>
            ) : null}
            {showHouseLabels && house.actualHouseLabel ? (
              <text x={houseX + 14} y={houseY - 8} fill={palette.actualHouse} fontSize="9" textAnchor="middle">
                {house.actualHouseLabel}
              </text>
            ) : null}
            {house.lagnaMarker ? (
              <text x={houseX + 16} y={houseY - 8} fill="#2e7d52" fontSize="9" textAnchor="middle" fontWeight="bold">
                {house.lagnaMarker}
              </text>
            ) : null}

            <text x={rashiX} y={rashiY} fill={palette.sign} fontSize="14" fontWeight="bold" textAnchor="middle">
              {house.signNumber}
            </text>
            {house.signLabel ? (
              <text x={rashiX} y={rashiY + 11} fill={palette.sign} fontSize="8" textAnchor="middle" opacity="0.7">
                {/* {house.signLabel} */}
              </text>
            ) : null}
            {house.secondaryLabel ? (
              <text x={rashiX} y={rashiY + 11} fill={palette.secondary} fontSize="9" textAnchor="middle">
                {house.secondaryLabel}
              </text>
            ) : null}

            {house.planets.map((planet, index, allPlanets) => {
              const textStyle = getPlanetTextStyle(allPlanets.length);
              const textY = getPlanetTextY(position.y, index, allPlanets.length);

              return (
                <g key={`${planet.abbr}-${planet.status || ''}-${planet.degree ?? ''}-${index}`}>
                  {planet.highlighted ? (
                    <circle
                      cx={planetX}
                      cy={textY - 5}
                      r="17"
                      fill="#fef3c7"
                      stroke="#f59e0b"
                      strokeWidth="1.2"
                      opacity="0.9"
                    />
                  ) : null}
                  {planet.isUpagraha ? (
                    <rect
                      x={planetX - 19}
                      y={textY - 17}
                      width="38"
                      height="21"
                      rx="5"
                      fill={isDark ? '#3b0764' : '#f3e8ff'}
                      stroke={isDark ? '#c084fc' : '#c4b5fd'}
                      strokeWidth="1"
                      opacity="0.88"
                    />
                  ) : null}
                  {planet.isTransit ? (
                    <rect
                      x={planetX - 25}
                      y={textY - 17}
                      width="50"
                      height="21"
                      rx="5"
                      fill={isDark ? '#082f49' : '#e0f2fe'}
                      stroke={isDark ? '#7dd3fc' : '#38bdf8'}
                      strokeWidth="1"
                      opacity="0.92"
                    />
                  ) : null}
                  <text
                    x={planetX}
                    y={textY}
                    fontSize={textStyle.fontSize}
                    fontWeight="800"
                    textAnchor="middle"
                    fill={isDark ? darkPlanetFallback(planet.color) : planet.color}
                  >
                    {planet.title ? <title>{planet.title}</title> : null}
                    <tspan>{planet.abbr}</tspan>
                    {planet.status ? (
                      <tspan dx="2" dy="-5" fontSize={textStyle.statusFontSize} fontWeight="700">
                        {planet.status}
                      </tspan>
                    ) : null}
                    {planet.degree !== null && planet.degree !== undefined ? (
                      <tspan dx="2" dy={planet.status ? 0 : -6} fontSize={textStyle.degreeFontSize} fontWeight="700">
                        {planet.degree}
                      </tspan>
                    ) : null}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

export default NorthIndianChartRenderer;
